import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  TextField,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { Send as SendIcon, Check, DoneAll } from "@mui/icons-material";
import { format, isToday, isYesterday, parseISO } from "date-fns";
import { getSocket } from "src/utils/socket";
import { useSelector } from "react-redux";
import { getLocalStorage, dataLocalStorage } from "src/helper/publicFunction";

// Helper function to format date for display
const formatMessageDate = (dateString) => {
  if (!dateString) return "Unknown date";

  try {
    const date = parseISO(dateString);

    if (isNaN(date.getTime())) return "Unknown date";

    if (isToday(date)) {
      return "Today";
    } else if (isYesterday(date)) {
      return "Yesterday";
    } else {
      return format(date, "MMM dd, yyyy");
    }
  } catch (error) {
    console.error("Error formatting date:", error);
    return "Unknown date";
  }
};

// Helper function to format time for display
const formatMessageTime = (dateString) => {
  if (!dateString) return "";

  try {
    const date = parseISO(dateString);

    if (isNaN(date.getTime())) return "";

    return format(date, "HH:mm");
  } catch (error) {
    console.error("Error formatting time:", error);
    return "";
  }
};

const Chat = ({ manifestUuid }) => {
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [socket, setSocket] = useState(null);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);

  // Get current user info
  const user =
    useSelector((state) => state?.user?.userinfo) ||
    getLocalStorage(dataLocalStorage.userinfo);
  const currentUserId = user?.uuid || user?.id;

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop =
        messagesContainerRef.current.scrollHeight;
    }
  };

  // Initialize socket connection
  useEffect(() => {
    console.log(
      "🔌 Chat component initializing with manifestUuid:",
      manifestUuid,
    );

    if (!manifestUuid) {
      setError("No manifest selected");
      return;
    }

    const apiUrl = import.meta.env.VITE_API_URL;
    console.log("🌐 API URL from env:", apiUrl);

    if (!apiUrl) {
      setError("API URL not configured");
      return;
    }

    setError(null);
    setIsLoading(true);

    console.log("🚀 Getting socket instance...");
    const socketInstance = getSocket(apiUrl);

    if (!socketInstance) {
      console.error("❌ Failed to get socket instance");
      setError("Failed to connect to chat server");
      setIsLoading(false);
      return;
    }

    console.log("✅ Socket instance created:", {
      id: socketInstance.id,
      connected: socketInstance.connected,
      disconnected: socketInstance.disconnected,
    });

    setSocket(socketInstance);

    // Listen for new messages
    const handleNewMessage = ({ message }) => {
      console.log("📨 New message received:", message);
      setMessages((prev) => {
        // Check if message already exists to avoid duplicates
        const exists = prev.some((m) => m.uuid === message.uuid);
        if (exists) return prev;
        return [...prev, message];
      });
      setTimeout(scrollToBottom, 100);
    };

    // Listen for messages history
    const handleMessages = ({
      manifestUuid: mUuid,
      messages: historyMessages,
    }) => {
      console.log("📚 Messages history received:", {
        manifestUuid: mUuid,
        count: historyMessages?.length || 0,
      });

      if (mUuid === manifestUuid) {
        setMessages(historyMessages || []);
        setIsLoading(false);
        setError(null);
        setTimeout(scrollToBottom, 100);
      }
    };

    // Listen for messages read events
    const handleMessagesRead = ({
      manifestUuid: mUuid,
      messages: updatedMessages,
    }) => {
      console.log("✓ Messages read event received:", {
        manifestUuid: mUuid,
        count: updatedMessages?.length || 0,
      });

      if (mUuid === manifestUuid && updatedMessages) {
        setMessages((prev) => {
          const messageMap = new Map(prev.map((m) => [m.uuid, m]));
          updatedMessages.forEach((msg) => {
            messageMap.set(msg.uuid, msg);
          });
          return Array.from(messageMap.values()).sort(
            (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
          );
        });
      }
    };

    socketInstance.on("new_message", handleNewMessage);
    socketInstance.on("messages", handleMessages);
    socketInstance.on("messages_read", handleMessagesRead);

    // Load message history
    console.log("📋 Requesting message history for manifest:", manifestUuid);

    // First, try to join the chat room
    socketInstance.emit("join", { room: manifestUuid }, (joinAck) => {
      console.log("📥 Join room acknowledgment:", joinAck);
    });

    socketInstance.emit("list_messages", { manifestUuid, limit: 50 }, (ack) => {
      console.log("📥 List messages acknowledgment:", ack);
      if (!ack?.success) {
        console.error("❌ Failed to load messages:", ack);
        // If chat doesn't exist, try to create/join it first
        if (ack?.error === "chat_not_found" || ack?.code === 404) {
          console.log(
            "🆕 Chat not found, attempting to create/join chat room...",
          );

          // Try to join the manifest chat (this will create it if it doesn't exist)
          socketInstance.emit(
            "join_manifest_chat",
            { manifestUuid },
            (joinAck) => {
              console.log("📥 Join manifest chat acknowledgment:", joinAck);
              if (joinAck?.success) {
                console.log("✅ Chat joined, retrying to load messages...");
                // Retry loading messages after joining
                socketInstance.emit(
                  "list_messages",
                  { manifestUuid, limit: 50 },
                  (retryAck) => {
                    if (!retryAck?.success) {
                      setError(
                        retryAck?.error ||
                          "Failed to load messages after joining chat",
                      );
                    } else {
                      setError(null);
                    }
                    setIsLoading(false);
                  },
                );
              } else {
                setError(joinAck?.error || "Failed to join chat");
                setIsLoading(false);
              }
            },
          );
        } else {
          setError(ack?.error || "Failed to load messages");
          setIsLoading(false);
        }
      } else {
        setError(null);
        setIsLoading(false);
      }
    });

    return () => {
      // Cleanup: Remove listeners but keep socket connected (admin auto-joins all chats)
      console.log("🧹 Cleaning up chat component listeners");
      socketInstance.off("new_message", handleNewMessage);
      socketInstance.off("messages", handleMessages);
      socketInstance.off("messages_read", handleMessagesRead);
    };
  }, [manifestUuid]);

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Handle send message
  const handleSendMessage = () => {
    console.log("🔍 Send message clicked:", {
      inputText: inputText.trim(),
      socket: !!socket,
      socketConnected: socket?.connected,
      manifestUuid,
      isSending,
    });

    if (!inputText.trim() || !socket || !manifestUuid || isSending) {
      console.log("❌ Cannot send message - validation failed:", {
        hasText: !!inputText.trim(),
        hasSocket: !!socket,
        socketConnected: socket?.connected,
        hasManifestUuid: !!manifestUuid,
        isSending,
      });
      return;
    }

    const messageText = inputText.trim();
    setIsSending(true);
    setError(null);

    console.log("📤 Emitting send_message with:", {
      manifestUuid,
      text: messageText,
    });

    socket.emit("send_message", { manifestUuid, text: messageText }, (ack) => {
      console.log("📥 Send message acknowledgment:", ack);
      setIsSending(false);
      if (!ack?.success) {
        console.error("❌ Failed to send message:", ack);
        setError(ack?.error || "Failed to send message");
        return;
      }

      const { message } = ack.data;
      console.log("✅ Message sent successfully:", message);
      // Message will be added via new_message event, but we can also add it optimistically
      setMessages((prev) => {
        const exists = prev.some((m) => m.uuid === message.uuid);
        if (exists) return prev;
        return [...prev, message];
      });
      setInputText("");
      setTimeout(scrollToBottom, 100);
    });
  };

  // Handle Enter key press
  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Group messages by date
  const groupedMessages = messages.reduce((acc, message) => {
    const dateKey = formatMessageDate(message.createdAt);
    if (!acc[dateKey]) {
      acc[dateKey] = [];
    }
    acc[dateKey].push(message);
    return acc;
  }, {});

  // Sort messages (ascending - oldest first)
  const sortedDateKeys = Object.keys(groupedMessages).sort((a, b) => {
    if (a === "Today") return 1;
    if (b === "Today") return -1;
    if (a === "Yesterday") return 1;
    if (b === "Yesterday") return -1;
    return (
      parseISO(groupedMessages[a][0].createdAt) -
      parseISO(groupedMessages[b][0].createdAt)
    );
  });

  // Sort messages within each date (ascending - oldest first)
  Object.keys(groupedMessages).forEach((dateKey) => {
    groupedMessages[dateKey].sort(
      (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
    );
  });

  if (!manifestUuid) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "400px",
          p: 3,
        }}
      >
        <Typography
          variant="body2"
          sx={{ color: "text.secondary", textAlign: "center" }}
        >
          No manifest selected
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: "400px",
        backgroundColor: "white",
        overflow: "hidden",
      }}
    >
      {/* Error Message */}
      {error && (
        <Box
          sx={{
            p: 1.5,
            mx: 2,
            mt: 2,
            backgroundColor: "#fee",
            border: "1px solid #fcc",
            borderRadius: 1,
            borderLeft: "3px solid #f44",
          }}
        >
          <Typography
            variant="caption"
            sx={{ color: "#c33", fontSize: "0.75rem" }}
          >
            {error}
          </Typography>
        </Box>
      )}

      {/* Messages Container */}
      <Box
        ref={messagesContainerRef}
        sx={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          p: { xs: 1.5, sm: 2 },
          display: "flex",
          flexDirection: "column",
          gap: 2,
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-track": {
            background: "#f1f1f1",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#c1c1c1",
            borderRadius: "3px",
            "&:hover": {
              background: "#a8a8a8",
            },
          },
        }}
      >
        {isLoading ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 6,
              gap: 2,
            }}
          >
            <CircularProgress size={32} sx={{ color: "#1e55bf" }} />
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Loading messages...
            </Typography>
          </Box>
        ) : error && sortedDateKeys.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 6,
              gap: 1,
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", textAlign: "center" }}
            >
              Unable to load messages
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", textAlign: "center" }}
            >
              {error}
            </Typography>
          </Box>
        ) : sortedDateKeys.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 6,
              gap: 1,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                textAlign: "center",
                fontWeight: 500,
              }}
            >
              No messages yet
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", textAlign: "center" }}
            >
              Start the conversation by sending a message
            </Typography>
          </Box>
        ) : (
          sortedDateKeys.map((dateKey) => (
            <Box key={dateKey}>
              {/* Date Header */}
              <Box
                sx={{
                  textAlign: "center",
                  mb: 1,
                  position: "sticky",
                  top: -20,
                  zIndex: 1,
                  backgroundColor: "white",
                  py: 1,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: "#1e55bf",
                    backgroundColor: "#e3f2fd",
                    borderRadius: 1,
                    px: 2,
                    py: 0.75,
                    display: "inline-block",
                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    border: "1px solid #bbdefb",
                  }}
                >
                  {dateKey}
                </Typography>
              </Box>

              {/* Messages for this date */}
              {groupedMessages[dateKey].map((message) => {
                const isAdmin = message.senderRole === "admin";
                const senderName = message.sender
                  ? `${message.sender.firstName || ""} ${
                      message.sender.lastName || ""
                    }`.trim() || "Admin"
                  : "Admin";

                // Check if message was sent by current user (using email matching)
                const currentUserEmail = user?.email?.toLowerCase();
                const senderEmail = message.sender?.email?.toLowerCase();
                const isSentByMe =
                  currentUserEmail &&
                  senderEmail &&
                  currentUserEmail === senderEmail;

                // Check if message has been read by recipient(s)
                const isReadByRecipient =
                  message.readBy && message.readBy.length > 0;

                // Show read receipt only for messages sent by current user
                const showReadReceipt = isSentByMe;

                return (
                  <Box
                    key={message.uuid}
                    sx={{
                      display: "flex",
                      mb: 2.5,
                      alignItems: "flex-start",
                      gap: 1.5,
                      p: { xs: 1, sm: 1.5 },
                      borderRadius: 2,
                      backgroundColor: isAdmin ? "#e3f2fd" : "#fff3e0",
                      transition: "background-color 0.2s",
                      "&:hover": {
                        backgroundColor: isAdmin ? "#bbdefb" : "#ffe0b2",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: "4px",
                        height: "4px",
                        borderRadius: "50%",
                        backgroundColor: "#1e55bf",
                        mt: 1,
                        flexShrink: 0,
                      }}
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: { xs: "0.8rem", sm: "0.875rem" },
                          lineHeight: 1.6,
                          mb: 0.5,
                        }}
                      >
                        <Box
                          component="span"
                          sx={{
                            fontWeight: 600,
                            color: "text.primary",
                            mr: 1,
                          }}
                        >
                          {senderName}
                        </Box>
                        <Box
                          component="span"
                          sx={{
                            color: "text.secondary",
                          }}
                        >
                          {message.text}
                        </Box>
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "text.secondary",
                          fontSize: { xs: "0.65rem", sm: "0.7rem" },
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        <Box component="span">
                          {formatMessageTime(message.createdAt)}
                        </Box>
                        {showReadReceipt && (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              ml: 0.5,
                            }}
                          >
                            {isReadByRecipient ? (
                              <DoneAll
                                sx={{
                                  fontSize: "0.8rem",
                                  color: "#4caf50",
                                }}
                              />
                            ) : (
                              <Check
                                sx={{
                                  fontSize: "0.8rem",
                                  color: "#9e9e9e",
                                }}
                              />
                            )}
                          </Box>
                        )}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          ))
        )}
        <div ref={messagesEndRef} />
      </Box>

      {/* Input Area */}
      <Box
        sx={{
          p: 2,
          backgroundColor: "#eeeeee99",
          borderRadius: 0,
          flexShrink: 0,
        }}
      >
        <Typography
          fontWeight="bold"
          color="primary"
          sx={{ display: "flex", alignItems: "center", mb: 1 }}
        >
          💬 Send Message
        </Typography>
        <Box
          sx={{
            display: "flex",
            gap: 1,
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <TextField
            fullWidth
            multiline
            maxRows={3}
            variant="outlined"
            placeholder="Type a message..."
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={isSending || !socket}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 1.5,
                backgroundColor: "#f8f9fa",
                fontSize: "0.875rem",
                "& fieldset": {
                  borderColor: "#e0e0e0",
                },
                "&:hover fieldset": {
                  borderColor: "#bdbdbd",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#1e55bf",
                  borderWidth: "1.5px",
                },
                "&.Mui-disabled": {
                  backgroundColor: "#f5f5f5",
                },
              },
            }}
          />
          <IconButton
            color="primary"
            onClick={handleSendMessage}
            disabled={!inputText.trim() || isSending || !socket}
            sx={{
              backgroundColor: "#1e55bf",
              color: "white",
              width: 40,
              height: 40,
              flexShrink: 0,
              "&:hover": {
                backgroundColor: "#1648a3",
              },
              "&.Mui-disabled": {
                backgroundColor: "#e0e0e0",
                color: "#9e9e9e",
              },
            }}
          >
            {isSending ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <SendIcon sx={{ fontSize: "1.1rem" }} />
            )}
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};

export default Chat;
